import type { MouseEventHandler, ReactNode } from 'react'

import { Button } from '@gto/ui/button'
import { Link } from '@gto/ui/link'

import { isAbsoluteHttpLink } from '../safe-link'
import { StoreBadgeLink } from '../store-badge-link'
import { modalCtaStore } from '../store-link'
import type { ModalCtaStyle } from './modal-block-types'

type ModalCtaBlockProps = {
  text: ReactNode
  /**
   * Absent draws the control with no destination — the admin preview's state for
   * a button that carries a label and no link yet. The block gate is the only
   * thing that can produce it; see `allowLinklessCta`.
   */
  link?: string
  style: ModalCtaStyle
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

const BUTTON_CLASS =
  'h-auto min-h-12 max-w-full whitespace-normal [overflow-wrap:anywhere] sm:w-full'

export const ModalCtaBlock = ({ text, link, style, onClick }: ModalCtaBlockProps) => {
  const color = style === 'secondary' ? 'secondary' : 'primary'

  // The blank test repeats the gate's own rather than trusting it: `href=""`
  // navigates back to the current page, so a caller that reaches this component
  // directly cannot produce a dead reload by passing the empty string.
  if (link === undefined || link.trim() === '') {
    // Rendered as a plain control rather than a dead `<a>`: an anchor with no
    // `href` is neither focusable nor a link to assistive technology, and the
    // point of the preview is to show the operator the real weight and colour.
    // It is not `disabled` either, for the same reason `PreviewCta` is not.
    return style === 'inline_link' ? (
      <Link
        as="span"
        underline="always"
      >
        {text}
      </Link>
    ) : (
      <Button
        className={BUTTON_CLASS}
        color={color}
        size="large"
        variant="contained"
      >
        {text}
      </Button>
    )
  }

  // An off-site destination opens in a new tab: it matches how every other
  // surface opens an absolute URL, and it leaves the app — and the dismissal
  // request the modal fires on click — alive instead of unloading both.
  const opensExternally = isAbsoluteHttpLink(link)
  const externalProps = opensExternally ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  if (style === 'inline_link') {
    // An inline link stays text even to a store: the operator picked the look
    // of a link inside the copy, and a badge would break out of it.
    return (
      <Link
        {...externalProps}
        href={link}
        underline="always"
        onClick={onClick}
      >
        {text}
      </Link>
    )
  }

  // A store listing draws the store's official badge instead of the button, on
  // every surface that renders a CTA, so the modal matches the rest.
  const store = modalCtaStore(style, link)

  if (store !== null) {
    return (
      <StoreBadgeLink
        {...externalProps}
        // Inline, like the button it replaces, so the card's `text-center` centres
        // it inside a row column too, where no flex parent does.
        className="inline-block align-top"
        href={link}
        label={typeof text === 'string' ? text : undefined}
        store={store}
        onClick={onClick}
      />
    )
  }

  return (
    <Button
      {...externalProps}
      className={BUTTON_CLASS}
      color={color}
      href={link}
      size="large"
      variant="contained"
      onClick={onClick}
    >
      {text}
    </Button>
  )
}
